import React, { useState, useRef, useEffect, useMemo } from "react";
import StoreFinder from "@src/stories/ecommerce/StoreFinder/StoreFinder";
import StoreFinderList from "@src/stories/ecommerce/StoreFinder/StoreFinderList";
import StoreFinderDetails from "@src/stories/ecommerce/StoreFinder/StoreFinderDetails";
import StoreFinderMap from "@src/stories/ecommerce/StoreFinder/StoreFinderMap";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import useLocationSearch from "@src/hooks/useLocationSearch";
import ToggleSwitch from "@src/stories/elements/ToggleSwitch/ToggleSwitch";

function getDistanceInKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function StoreLocatorPage() {
  const inputRef = useRef(null);
  const [query, setQuery] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showList, setShowList] = useState(true);
  const isMobile = useMobileBreakpoint("1024px");

  const {
    stores,
    loading,
    searchByQuery,
    handleLocationSearch,
    userCoords,
    hasManualSearch,
  } = useLocationSearch(inputRef);

  const handleSelectStore = (store) => setSelectedStore(store);
  const handleBack = () => {
    setSelectedStore(null);
    if (isMobile) setShowList(true);
  };
  const handleToggleChange = (isChecked) => setShowList(isChecked);

  const handleSearch = (e) => {
    setQuery(e);
    searchByQuery(e);
  }

  useEffect(() => {
    if (!selectedStore && !hasManualSearch) {
      handleLocationSearch();
    }
  }, [selectedStore, hasManualSearch]);

  const filteredStoresList = useMemo(() => {
    if (!Array.isArray(stores)) return [];

    if (!hasManualSearch) return stores;

    if (userCoords) {
      const userLat = parseFloat(userCoords.lat);
      const userLng = parseFloat(userCoords.lng);

      const filtered = stores.filter((store) => {
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);

        if ([lat, lng, userLat, userLng].some(isNaN)) {
          console.warn(`Skipping ${store.name} due to invalid coordinates`);
          return false;
        }

        const dist = getDistanceInKm(userLat, userLng, lat, lng);
        return dist <= 10;
      });

      return filtered.length > 0 ? filtered : stores;
    }

    return stores;
  }, [stores, hasManualSearch, userCoords]);

  return (
    <div className="mt-[40px] z-[1] lg:mt-[35px] lg:mb-[165px] lg:relative">
      {(!selectedStore || !isMobile) && (
        <>
          <div className="max-w-[1048px] mx-auto px-4 lg:px-0 mb-6 lg:mb-[64px]">
            <StoreFinder
              layout="store-locator"
              inputRef={inputRef}
              onSearch={(e) => handleSearch(e)}
            />
          </div>
          <div className="flex items-center justify-center gap-4 mb-3 lg:hidden">
            <div>Map</div>
            <ToggleSwitch onChange={handleToggleChange} initialChecked={true} />
            <div>List</div>
          </div>
        </>
      )}

      <div className="flex flex-col gap-5 lg:mb-0 lg:flex-row">
        <div className="shrink-0 lg:flex-none lg:pl-5 lg:max-w-[640px] mb-[68px] lg:mb-0">
          {selectedStore ? (
            <StoreFinderDetails
              store={selectedStore}
              onBack={handleBack}
              hasManualSearch={hasManualSearch}
            />
          ) : (
            <StoreFinderList
              showList={showList}
              storesData={filteredStoresList}
              onSelectStore={handleSelectStore}
              hasManualSearch={hasManualSearch}
              loading={loading}
            />
          )}
        </div>

        {((!showList && !selectedStore && isMobile) || !isMobile) && (
          <StoreFinderMap
            storesData={filteredStoresList}
            selectedStore={selectedStore}
            setSelectedStore={setSelectedStore}
            query={query}
          />
        )}
      </div>
    </div>
  );
}

export default StoreLocatorPage;